import { bind, classNames, Component, h, signal, tag } from "omi";

import { NWIcon } from "@/components/atoms/nw-icon";
import { NWTooltip } from "@/components/atoms/nw-tooltip";
import { closeModal, openProModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import "../../../ui/themePreview";

import { DocumentationTheme } from "@/api/models/documentationTheme";

import { BasePreviewImage } from "../../../ui/themePreview";
import { isProTheme } from "../../../models";

type Props = {
  selected?: DocumentationTheme;
  isProcessing: boolean;
  onThemeSelect: (theme: DocumentationTheme) => void;
};

@tag("share-options-theme-selector")
export class ThemeSelector extends Component<Props> {
  static css = [tailwind];
  private readonly THEMES = [DocumentationTheme.LIGHT, DocumentationTheme.DARK, DocumentationTheme.FUN];

  private previewTheme = signal<DocumentationTheme | undefined>(undefined);

  private getIcon(theme: DocumentationTheme) {
    switch (theme) {
      case DocumentationTheme.LIGHT:
        return <LightThemeIcon />;
      case DocumentationTheme.DARK:
        return <DarkThemeIcon />;
      case DocumentationTheme.FUN:
        return <FunThemeIcon />;
    }
  }

  @bind
  private handleOnClick(theme: DocumentationTheme) {
    if (isProTheme(theme) && !Store.user.isPro()) {
      closeModal();
      setTimeout(() => openProModal("project_share_theme_select"), 300);
      return;
    }

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackDocumentationThemeClicked(theme);
    });

    this.props.onThemeSelect(theme);
  }

  @bind
  private renderThemeCard(theme: DocumentationTheme) {
    const isProExclusive = isProTheme(theme);
    const isAvailableToUser = (isProExclusive && Store.user.isPro()) || !isProExclusive;

    return (
      <NWTooltip
        content="Unlock with PRO"
        placement="top"
        trigger="click"
        strategy="absolute"
        shouldShow={!isAvailableToUser}
      >
        <button
          data-theme={theme}
          className={classNames("flex flex-col items-center rounded-lg px-2 max-w-32 relative group", {
            "cursor-not-allowed": this.props.isProcessing,
          })}
          onClick={() => this.handleOnClick(theme)}
          onMouseEnter={() => (this.previewTheme.value = theme)}
          onMouseLeave={() => (this.previewTheme.value = undefined)}
          disabled={this.props.isProcessing}
        >
          <div
            className={classNames(
              "relative rounded-full border border-transparent transition-colors duration-300 group-hover:bg-graysecondary-100 p-4",
              {
                "bg-graysecondary-50 !border-brand-300": theme === this.props.selected,
              },
            )}
          >
            {this.getIcon(theme)}
            {!isAvailableToUser && (
              <div class="absolute inset-0 flex items-center justify-center">
                <NWIcon name="lock" width={22} height={22} class="text-white"></NWIcon>
              </div>
            )}
          </div>
          <span className="text-xs text-[#717680] mt-2 capitalize">{theme}</span>
        </button>
      </NWTooltip>
    );
  }

  render() {
    return (
      <div class="relative">
        <BasePreviewImage theme={this.previewTheme.value} />

        <div className="flex items-center justify-between py-4">{this.THEMES.map(t => this.renderThemeCard(t))}</div>
      </div>
    );
  }
}

function LightThemeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 40 40" width={40} height={40}>
      <path
        fill="#EBE8E4"
        fill-rule="evenodd"
        d="M19.9999.502467A19.764057 19.764057 0 0 0 19.6875.5C8.81439.5 0 9.4543 0 20.5s8.81439 20 19.6875 20c.1043 0 .2085-.0008.3124-.0025V.502467Z"
        clip-rule="evenodd"
      />
      <path
        fill="#F8F5F1"
        fill-rule="evenodd"
        d="M19.9999.50247C20.1039.500825 20.2081.5 20.3125.5 31.1856.5 40 9.4543 40 20.5s-8.8144 20-19.6875 20c-.1044 0-.2086-.0008-.3126-.0025V.50247Z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function DarkThemeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 40 40" width={40} height={40}>
      <path
        fill="#4F4946"
        fill-rule="evenodd"
        d="M19.9999.002467A19.765902 19.765902 0 0 0 19.6875 0C8.81439 0 0 8.9543 0 20s8.81439 20 19.6875 20c.1043 0 .2085-.0008.3124-.0025V.002467Z"
        clip-rule="evenodd"
      />
      <path
        fill="#1B1918"
        fill-rule="evenodd"
        d="M19.9999.00247C20.1039.000825 20.2081 0 20.3125 0 31.1856 0 40 8.9543 40 20s-8.8144 20-19.6875 20c-.1044 0-.2086-.0008-.3126-.0025V.00247Z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function FunThemeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 40 40" width={40} height={40}>
      <mask id="a" width="40" height="40" x="0" y="0" maskUnits="userSpaceOnUse" style="mask-type:alpha">
        <ellipse cx="19.6875" cy="20" fill="#D9D9D9" rx="19.6875" ry="20" transform="matrix(-1 0 0 1 39.375 0)" />
      </mask>
      <g mask="url(#a)">
        <path
          fill="#FFEF99"
          fill-rule="evenodd"
          d="M18.9999.002467A19.765902 19.765902 0 0 0 18.6875 0C7.81439 0-1 8.9543-1 20s8.81439 20 19.6875 20c.1043 0 .2085-.0008.3124-.0025V.002467Z"
          clip-rule="evenodd"
        />
        <path
          fill="#FA8A45"
          d="M28.9387 24.725c-4.2 5.46-1.4653 11.6677-.0653 14.2926 1.9251 2.275 7.5208 3.7674 5.8408-3.7926-1.68-7.56 4.2-14.3499 7.35-16.7999-.175-2.275-2.7956-5.4474-3.2156-4.6074-.525 1.05-4.6599 4.0824-9.9099 10.9073Z"
        />
        <path
          fill="#FEDD2D"
          d="M39.4121 19.6633c-6.7656 4.2291-7.0439 13.5567-6.825 16.7999l.5247-.525c5.8439-5.9308 12.7463-20.3043 6.3003-16.2749Z"
        />
        <path
          fill="#965ECD"
          d="M30.9646 22.6364c-5.88 6.3-3.85 13.475-2.1 16.275l-2.1 1.05c-1.575-1.575-2.625-7.875-2.625-11.55 0-3.675.4512-5.7637 6.3-11.55 5.8488-5.7862 3.15-11.54996 4.2-11.02496.84.42 4.2738 5.76366 4.2 7.87496-.0738 2.1113-1.995 2.625-7.875 8.925Z"
        />
        <path
          fill="#308DED"
          d="M19 29v10.9501c2.1001 0 8.4999.84 9.3399 0 0-.525-1.575-2.625-2.625-8.4s-.5988-8.3887 3.675-12.075c4.2738-3.6863 7.875-11.02495 6.3-13.64994-1.575-2.625-2.625-3.15-8.4-5.77499 3.15 4.19999 3.15 8.39998 2.1 12.59993C28.3399 16.8501 19 20.0596 19 29Z"
        />
        <path
          fill="#12CCA6"
          d="m19 29.5-.1105-13.6886c6.8252-2.1 6.6584-4.1609 5.775-7.87495-1.5011-6.31126-6.3-8.924975-5.775-8.924975.525 0 4.7521.443933 6.8986 1.038718 3.7486 1.038717 9.975 6.824977 2.625 17.324907C24.3257 23.2143 18.9789 26.8802 19 29.5Z"
        />
        <path
          fill="#FA8A45"
          d="M25.2644 9.50013c0 5.45997-4.6238 5.96127-6.3738 6.31127V-.988594c0 .524999 6.3738 3.663744 6.3738 10.488724Z"
        />
      </g>
    </svg>
  );
}
